import { createElement, useMemo, type ReactNode } from "react"
import { IntlProvider } from "react-intl"
import { pseudoLocalizeAll } from "@/lib/pseudo-locale"

export const PSEUDO_LOCALE = "en-XA"

// Catalogs arrive on full page loads only (see ApplicationController), so keep
// the last one seen per locale for the Inertia visits that follow.
const catalogs = new Map<string, Record<string, string>>()
let lastCatalog: Record<string, string> = {}

type I18nProps = {
  locale?: string
  messages?: Record<string, string> | null
  children?: ReactNode
}

/** Gives every page the active locale and its messages via react-intl. */
export function I18nProvider({ locale = "en", messages, children }: I18nProps) {
  if (messages) catalogs.set(locale, messages)
  // If a visit ever arrives without this locale's catalog, keep the last one
  // rather than showing raw message ids.
  const catalog = messages ?? catalogs.get(locale) ?? lastCatalog
  if (messages) lastCatalog = messages

  const resolved = useMemo(() => (locale === PSEUDO_LOCALE ? pseudoLocalizeAll(catalog) : catalog), [locale, catalog])

  return createElement(
    IntlProvider,
    {
      locale,
      defaultLocale: "en",
      messages: resolved,
      // A key missing from every locale is a bug to fix in the catalog, not a
      // crash; the id shows in the UI and the console.
      onError: (err) => {
        if (err.code !== "MISSING_TRANSLATION") console.error(err)
      },
    },
    children,
  )
}
